// 📑 Tabs Store - Sistema de pestañas dinámico
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

export interface Tab {
  id: string;
  title: string;
  path: string;
  closable: boolean;
  icon?: string;
  formData?: any; // Datos del formulario para persistencia
}

interface TabsStore {
  tabs: Tab[];
  activeTabId: string;
  ownerUserId: string | null; // usuario dueño de las pestañas persistidas
  ensureOwner: (userId: string | null) => void; // limpia si cambió el usuario
  openTab: (tab: Omit<Tab, 'closable'> & { closable?: boolean }) => void;
  closeTab: (tabId: string) => void;
  setActiveTab: (tabId: string) => void;
  reorderTabs: (tabs: Tab[]) => void;
  closeAllTabs: () => void;
  closeOtherTabs: (tabId: string) => void;
  closeTabsToRight: (tabId: string) => void;
  updateTab: (tabId: string, updates: Partial<Tab>) => void;
  setTabFormData: (tabId: string, formData: any) => void;
  getTabFormData: (tabId: string) => any;
}

// Ya no hay un Dashboard fijo: el landing es la primera opción del menú del perfil
// (lo abre AppContent al cargar los menús). Las pestañas arrancan vacías.

export const useTabsStore = create<TabsStore>()(
  persist(
    (set, get) => ({
      tabs: [],
      activeTabId: '',
      ownerUserId: null,

      // Las pestañas quedan atadas al usuario que las abrió. Al iniciar sesión con OTRO
      // usuario se cierran todas; con el mismo usuario se conservan.
      ensureOwner: (userId) => {
        if (!userId) return;
        if (get().ownerUserId === userId) return;
        set({ tabs: [], activeTabId: '', ownerUserId: userId });
      },

      openTab: (tab) => {
        const { tabs } = get();
        const existingTab = tabs.find((t) => t.id === tab.id);

        if (existingTab) {
          // Si ya existe, solo activarla
          set({ activeTabId: tab.id });
        } else {
          // Agregar nueva tab
          set({
            tabs: [
              ...tabs,
              {
                ...tab,
                closable: tab.closable !== undefined ? tab.closable : true,
              },
            ],
            activeTabId: tab.id,
          });
        }
      },

      closeTab: (tabId) => {
        const { tabs, activeTabId } = get();
        const tabToClose = tabs.find((t) => t.id === tabId);

        // No cerrar si no es closable
        if (!tabToClose?.closable) return;

        const newTabs = tabs.filter((t) => t.id !== tabId);

        // Si cerramos la tab activa, activar la anterior o la siguiente
        let newActiveTabId = activeTabId;
        if (activeTabId === tabId) {
          const closedIndex = tabs.findIndex((t) => t.id === tabId);
          const newTab = newTabs[closedIndex - 1] || newTabs[0];
          newActiveTabId = newTab?.id ?? '';
        }

        set({
          tabs: newTabs,
          activeTabId: newActiveTabId,
        });
      },

      setActiveTab: (tabId) => {
        const { tabs } = get();
        const tab = tabs.find((t) => t.id === tabId);
        if (tab) {
          set({ activeTabId: tabId });
        }
      },

      reorderTabs: (newTabs) => set({ tabs: newTabs }),

      closeAllTabs: () => {
        // Solo mantener tabs no closables
        const { tabs } = get();
        const nonClosableTabs = tabs.filter((t) => !t.closable);
        set({
          tabs: nonClosableTabs,
          activeTabId: nonClosableTabs[0]?.id || '',
        });
      },

      closeOtherTabs: (tabId) => {
        const { tabs } = get();
        const tabToKeep = tabs.find((t) => t.id === tabId);
        const nonClosableTabs = tabs.filter((t) => !t.closable);

        if (tabToKeep) {
          set({
            tabs: [...nonClosableTabs, ...(tabToKeep.closable ? [tabToKeep] : [])],
            activeTabId: tabId,
          });
        }
      },

      // Cierra las pestañas cerrables que estén a la DERECHA de la indicada
      // (conserva las no cerrables). Mantiene la activa si sobrevive; si no, activa la indicada.
      closeTabsToRight: (tabId) => {
        const { tabs, activeTabId } = get();
        const idx = tabs.findIndex((t) => t.id === tabId);
        if (idx < 0) return;
        const newTabs = tabs.filter((t, i) => i <= idx || !t.closable);
        const stillActive = newTabs.some((t) => t.id === activeTabId);
        set({ tabs: newTabs, activeTabId: stillActive ? activeTabId : tabId });
      },

      updateTab: (tabId, updates) => {
        const { tabs } = get();
        set({
          tabs: tabs.map((t) => (t.id === tabId ? { ...t, ...updates } : t)),
        });
      },

      setTabFormData: (tabId, formData) => {
        const { tabs } = get();
        set({
          tabs: tabs.map((t) =>
            t.id === tabId ? { ...t, formData } : t
          ),
        });
      },

      getTabFormData: (tabId) => {
        const { tabs } = get();
        const tab = tabs.find((t) => t.id === tabId);
        return tab?.formData;
      },
    }),
    {
      name: 'tabs-storage',
      // Aislado POR PESTAÑA: cada pestaña del navegador tiene su propio
      // escritorio de pestañas (no se contamina con otras empresas/sesiones
      // abiertas en paralelo) y sobrevive su propio F5.
      storage: createJSONStorage(() => sessionStorage),
      version: 2,  // v2: se eliminó el Dashboard fijo
      // Limpia el estado guardado de versiones previas que tenían la pestaña Dashboard fija.
      migrate: (persisted: any) => {
        if (!persisted) return persisted;
        const tabs = (persisted.tabs || []).filter((t: Tab) => t.id !== 'dashboard' && t.path !== '/dashboard');
        let activeTabId = persisted.activeTabId;
        if (activeTabId === 'dashboard' || !tabs.some((t: Tab) => t.id === activeTabId)) {
          activeTabId = tabs[0]?.id ?? '';
        }
        return { ...persisted, tabs, activeTabId };
      },
      partialize: (state) => ({
        tabs: state.tabs,
        activeTabId: state.activeTabId,
        ownerUserId: state.ownerUserId,
      }),
    }
  )
);
